import React, { useState, useEffect } from 'react';
import { Button, Card, Alert } from 'antd';
import PropTypes from 'prop-types';

const mapStyles = `
@keyframes pulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.05); opacity: 0.9; }
}
@keyframes iconPulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.8; }
}
.pulse-icon {
  animation: iconPulse 1.5s ease-in-out infinite;
  display: inline-block;
}
.location-map-card {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}
.location-map-header {
  background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
  padding: 16px 20px;
  color: white;
}
.map-container {
  transition: all 0.3s ease;
  border-radius: 8px;
}
.map-container.editable {
  background: linear-gradient(135deg, #e6f7ff 0%, #f0f5ff 100%);
}
.drag-item {
  transition: box-shadow 0.2s ease;
  cursor: move;
  border-radius: 8px;
  font-weight: 600;
  backdrop-filter: blur(2px);
}
.drag-item:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.2);
}
`;

const LocationMap = props => {
  const [locations, setLocation] = useState(props.locations);
  const [isEditable, setEditable] = useState(false);

  const setNewPosition = (position, id) => {
    const locationIndex = locations.findIndex(loc => loc.id === id);
    const newLocations = [...locations];
    const { x: x_position, y: y_position } = position;
    if (locationIndex !== -1) {
      const location = {
        x_position,
        y_position,
      };
      newLocations[locationIndex] = { ...newLocations[locationIndex], ...location };
      setLocation(newLocations);
    }
  };

  const setNewSize = (position, { width, height }, id) => {
    const locationIndex = locations.findIndex(loc => loc.id === id);
    const newLocations = [...locations];
    const { x: x_position, y: y_position } = position;
    if (locationIndex !== -1) {
      const location = {
        x_position,
        y_position,
        width,
        height,
      };
      newLocations[locationIndex] = { ...newLocations[locationIndex], ...location };
      setLocation(newLocations);
    }
  };

  useEffect(() => {
    setLocation(props.locations);
  }, [props.locations, isEditable]);
  
  return (
    <>
      <style>{mapStyles}</style>
      <Card className="location-map-card" bordered={false}>
        <div className="location-map-header">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span className="pulse-icon" style={{ fontSize: 28 }}>🗺️</span>
              <div>
                <h3 style={{ margin: 0, color: 'white', fontWeight: 700 }}>LOCATION MAP</h3>
                <p style={{ margin: '4px 0 0', fontSize: 12, opacity: 0.8 }}>
                  {locations.filter(l => l.showOnMap).length} active locations
                </p>
              </div>
            </div>
            <div>
              <Button 
                style={{ marginRight: 10 }}
                onClick={() => setEditable(val => !val)} 
                icon={isEditable ? 'close-circle' : 'edit'}
                type={isEditable ? 'default' : 'primary'}
              >
                {isEditable ? 'CANCEL EDIT' : 'EDIT LOCATION MAP'}
              </Button>
              {isEditable && (
                <Button 
                  icon="save" 
                  type="primary"
                  onClick={() => props.updateMapPosition(locations, setEditable)}
                >
                  SAVE CHANGES
                </Button>
              )}
            </div>
          </div>
        </div>
        
        {isEditable && (
          <Alert
            message="Edit Mode Active"
            description="Drag to reposition items or resize using the corners. Click SAVE to confirm changes."
            type="info"
            showIcon
            style={{ margin: '16px 20px 0', borderRadius: 8 }}
          />
        )}
        
        <div
          className={`map-container ${isEditable ? 'editable' : ''}`}
          style={{
            width: '100%',
            height: 'calc(100vh - 200px)',
            minHeight: 500,
            marginTop: 20,
            overflow: 'auto',
            position: 'relative',
            background: isEditable 
              ? 'linear-gradient(135deg, #e6f7ff 0%, #f0f5ff 100%)' 
              : '#fff',
            border: '2px solid #e8e8e8',
            borderRadius: 8,
          }}
        >
          {locations
            .filter(location => location.showOnMap)
            .map(location => {
              const { Rnd } = require('react-rnd');
              return (
                <Rnd
                  key={location.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: isEditable 
                      ? 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)'
                      : 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                    color: '#fff',
                    borderRadius: 8,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    fontWeight: 600,
                    fontSize: 14,
                    cursor: isEditable ? 'move' : 'default',
                    transition: 'all 0.2s ease',
                  }}
                  position={{ x: location.x_position, y: location.y_position }}
                  size={{ width: location.width, height: location.height }}
                  minWidth={50}
                  minHeight={50}
                  onDragStop={(e, data) => setNewPosition(data, location.id)}
                  onResizeStop={(e, direction, { style }, delta, position) =>
                    setNewSize(position, style, location.id)
                  }
                  onResize={e => e.stopPropagation()}
                  bounds="parent"
                  enableResizing={{
                    top: isEditable,
                    left: isEditable,
                    right: isEditable,
                    bottom: isEditable,
                    bottomLeft: isEditable,
                    bottomRight: isEditable,
                    topLeft: isEditable,
                    topRight: isEditable,
                  }}
                  disableDragging={!isEditable}
                  className="drag-item"
                >
                  <span style={{ padding: '0 8px' }}>📍 {location.label}</span>
                </Rnd>
              );
            })}
        </div>
      </Card>
    </>
  );
};

LocationMap.propTypes = {
  locations: PropTypes.arrayOf(PropTypes.object).isRequired,
  updateMapPosition: PropTypes.func,
};

export default LocationMap;